import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, IconButton, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerToolbarTheme, brandGradient, ON_BRAND } from '../../theme/plannerTheme';
import AssessmentIcon from '@material-ui/icons/Assessment';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import PersonIcon from '@material-ui/icons/Person';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import GavelIcon from '@material-ui/icons/Gavel';
import AssignmentIcon from '@material-ui/icons/Assignment';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TodayIcon from '@material-ui/icons/Today';
import PrintIcon from '@material-ui/icons/Print';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import BuildIcon from '@material-ui/icons/Build';
import { AuthContext } from '../../App';
import { getCurrencySymbol } from '../../utils/currencies';
import { isMoveExpressTenant } from '../../utils/manageHelpers';
import { useSelector, useDispatch } from 'react-redux';
import { selectUI, setActiveView } from '../../store/slices/uiSlice';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';

// ── Exportação Excel dedicada ao perfil combustível (Move Express) ──────
// O cliente quer um .xlsx em que CADA COLUNA é uma coluna da BD, bem
// separada e com cabeçalho legível (a exportação genérica com json_to_sheet
// despejava os objetos crus da OT — campos aninhados e chaves técnicas).
// Cada entrada = [cabeçalho PT, acessor(linha)]. Valores sempre escalares.
const xNum = (v) => (v == null || v === '' ? '' : Number(v));
const xDateTime = (v) => (v ? new Date(v).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const xDate = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '');
const xBool = (v) => (v == null ? '' : (v ? 'Sim' : 'Não'));

// ── Rótulos traduzidos (partilhados por ecrã, Excel e PDF) ──────────────
// Estado e Fase vêm crus (inglês) do backend. Os mapas replicam exactamente
// os que a app já usa (OrdersView: STATUS_KEYS/i18n + FUEL_STAGE_BUCKETS), para
// os relatórios não divergirem do resto da plataforma.
const OT_STATUS_LABELS = {
  pending:    { pt: 'Por Atribuir', en: 'Unassigned' },
  assigned:   { pt: 'Atribuída',    en: 'Assigned' },
  in_transit: { pt: 'Em Trânsito',  en: 'In Transit' },
  delivered:  { pt: 'Entregue',     en: 'Delivered' },
  cancelled:  { pt: 'Cancelada',    en: 'Cancelled' },
};
// arrived_pa e unloading partilham "No P.A. / a entregar" (igual ao bucket).
const FUEL_STAGE_LABELS = {
  departed:         { pt: 'A caminho do terminal',    en: 'To terminal' },
  arrived_terminal: { pt: 'No terminal / a carregar', en: 'At terminal' },
  loaded:           { pt: 'A caminho do P.A.',        en: 'To PA' },
  arrived_pa:       { pt: 'No P.A. / a entregar',     en: 'At PA' },
  unloading:        { pt: 'No P.A. / a entregar',     en: 'At PA' },
  completed:        { pt: 'A regressar',              en: 'Returning' },
  returned:         { pt: 'Concluída',                en: 'Done' },
};
// A fonte padrão do jsPDF (helvetica) só desenha Latin-1/WinAnsi — emojis e
// pictogramas saem como lixo ("Ø=Þ"). Limpa-os SÓ no PDF (ecrã e Excel mantêm
// os emojis). Mantém acentos (á, ç, ã) e travessão (—), que são suportados.
const sanitizePdfText = (v) => {
  if (v == null || v === '') return '—';
  const s = String(v)
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return s || '—';
};

const otStatusText = (s, pt = true) => { const e = OT_STATUS_LABELS[s]; return e ? (pt ? e.pt : e.en) : (s || ''); };
// fuelStage vazio/nulo = ainda na base (default do bucket na_base).
const fuelStageText = (s, pt = true) => { if (!s) return pt ? 'Na base' : 'At base'; const e = FUEL_STAGE_LABELS[s]; return e ? (pt ? e.pt : e.en) : s; };

// Ordens de Transporte (OT de combustível) — colunas da tabela transport_orders.
const FUEL_ORDER_COLUMNS = [
  ['Nº Ordem', o => o.orderNumber || ''],
  ['Referência', o => o.customRef || ''],
  ['Ref. Externa', o => o.externalRef || ''],
  ['Estado', o => otStatusText(o.status)],
  ['Fase', o => fuelStageText(o.fuelStage)],
  ['Produto', o => o.fuelProduct || ''],
  ['Litros carregados', o => xNum(o.loadingOrderQty)],
  ['Volume (m³)', o => (o.loadingOrderQty != null ? Math.round(Number(o.loadingOrderQty) / 1000 * 100) / 100 : '')],
  ['Litros previstos', o => xNum(o.expectedLitres)],
  ['Litros descarregados', o => xNum(o.deliveryGuideQty)],
  // Divergência = carga - total descarregado (soma dos 2 postos quando dividida).
  ['Divergência (L)', o => {
    if (o.loadingOrderQty == null) return '';
    const total = o.splitDelivery
      ? (Number(o.deliveryGuideQty || 0) + Number(o.deliveryGuideQty2 || 0))
      : (o.deliveryGuideQty != null ? Number(o.deliveryGuideQty) : null);
    return total != null ? Number(o.loadingOrderQty) - total : '';
  }],
  ['Cliente final', o => o.fuelCustomer || ''],
  ['Recolha', o => o.pickupName || ''],
  ['Morada recolha', o => o.pickupAddress || ''],
  ['Entrega (P.A.)', o => o.deliveryName || ''],
  ['Morada entrega', o => o.deliveryAddress || ''],
  ['Província', o => o.deliveryProvince || ''],
  // ── Entrega dividida por 2 P.A. (Fase B) ──
  ['Entrega dividida', o => xBool(o.splitDelivery)],
  ['Litros planeados P.A.1', o => xNum(o.plannedQty1)],
  ['Litros planeados P.A.2', o => xNum(o.plannedQty2)],
  ['Entrega (P.A. 2)', o => o.deliveryName2 || ''],
  ['Morada entrega 2', o => o.deliveryAddress2 || ''],
  ['Guia entrega P.A.2', o => o.deliveryGuideRef2 || ''],
  ['Litros descarregados P.A.2', o => xNum(o.deliveryGuideQty2)],
  ['Próximo destino', o => o.nextDestination || ''],
  ['Viatura', o => o.vehiclePlate || ''],
  ['Cisterna', o => o.cisternPlate || ''],
  ['Cap. cisterna (m³)', o => xNum(o.cisternCapacityM3)],
  ['Motorista', o => o.driverName || ''],
  ['Nº Ordem carga', o => o.loadingOrderRef || ''],
  ['Nr. Pedido SNLD', o => o.loadingPedidoSnld || ''],
  ['Doc. Ref', o => o.loadingDocRef || ''],
  ['Cód. instalação', o => o.loadingInstal || ''],
  ['Cód. material', o => o.loadingMaterialCode || ''],
  ['Nº selo', o => o.loadingSealNumber || ''],
  ['Guia entrega', o => o.deliveryGuideRef || ''],
  ['Selo intacto', o => xBool(o.deliverySealIntact)],
  // Espera na fila do terminal = início de carregamento − chegada ao terminal.
  ['Espera terminal (min)', o => {
    const a = o.assistanceReport?.arrivedAt, s = o.assistanceReport?.loadingStartedAt;
    if (!a || !s) return '';
    const m = Math.round((new Date(s) - new Date(a)) / 60000);
    return m >= 0 ? m : '';
  }],
  // Cronologia no P.A. — chegada e saída (esta última auto-detetada por GPS).
  ['Chegada ao P.A.', o => xDateTime(o.assistanceReport?.arrivedPaAt)],
  ['Saída do P.A.', o => xDateTime(o.assistanceReport?.departedPaAt)],
  // Tempo no P.A. = saída − chegada (min).
  ['Tempo no P.A. (min)', o => {
    const a = o.assistanceReport?.arrivedPaAt, d = o.assistanceReport?.departedPaAt;
    if (!a || !d) return '';
    const m = Math.round((new Date(d) - new Date(a)) / 60000);
    return m >= 0 ? m : '';
  }],
  ['Preço acordado', o => xNum(o.agreedPrice)],
  ['Moeda', o => o.currency || ''],
  ['Distância (km)', o => xNum(o.distanceKm)],
  ['Data serviço', o => xDate(o.serviceDate)],
  ['Criada em', o => xDateTime(o.createdAt)],
  ['Entregue em', o => xDateTime(o.deliveredAt)],
];

// Relatório de Veículos (costs-by-vehicle) — uma coluna por métrica.
const FUEL_VEHICLE_COLUMNS = [
  ['Matrícula', v => v.plate || ''],
  ['Tipo', v => v.type || ''],
  ['Nº Rotas', v => (v.routeCount ?? 0)],
  ['Distância (km)', v => (v.totalDistance != null ? Math.round(v.totalDistance / 1000) : '')],
];

// Nome de separador válido no Excel: máx 31 chars, sem : \ / ? * [ ].
function sanitizeSheetName(name) {
  return (name || 'Relatório').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Relatório';
}

// Monta a folha final a partir de [cabeçalho, ...linhas], acrescentando no topo
// o NOME do relatório e a data de geração (pedido Move Express — o nome não
// aparecia na exportação). Devolve { aoa, widths } pronto para aoa_to_sheet.
function buildTitledSheet(title, header, body) {
  const ncols = Math.max(1, header.length);
  const spacer = new Array(ncols).fill('');
  const titleRow = [title, ...spacer.slice(1)];
  const dateRow = [`Gerado em ${new Date().toLocaleString('pt-PT')}`, ...spacer.slice(1)];
  const aoa = [titleRow, dateRow, spacer, header, ...body];
  const widths = header.map((h, i) => {
    const bodyMax = body.reduce((m, r) => Math.max(m, String(r[i] ?? '').length), 0);
    return { wch: Math.max(12, String(h).length + 2, Math.min(48, bodyMax + 2)) };
  });
  // Título ocupa a largura toda (merge da 1ª linha).
  const merges = ncols > 1 ? [{ s: { r: 0, c: 0 }, e: { r: 0, c: ncols - 1 } }] : [];
  return { aoa, widths, merges };
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%',
    background: 'var(--surf-page, #f8fafc)', color: 'var(--surf-text, inherit)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    // Inputs de filtro (MUI) seguem o tema via tokens --surf-input-*; no Clássico
    // (sem data-planner-theme) os var() usam o fallback = aspeto MUI original.
    '& .MuiOutlinedInput-root': { background: 'var(--surf-input-bg, transparent)' },
    '& .MuiOutlinedInput-input, & .MuiSelect-root': { color: 'var(--surf-text, inherit)' },
    '& .MuiInputLabel-root': { color: 'var(--surf-muted, #64748b)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--surf-input-border, rgba(0,0,0,0.23))' },
    '& .MuiSelect-icon': { color: 'var(--surf-muted, #64748b)' },
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px', background: 'var(--surf-card, #fff)',
    borderBottom: '1px solid var(--surf-border, #e2e8f0)',
  },
  content: { flex: 1, overflowY: 'auto', padding: 24 },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
  },
  card: {
    background: 'var(--surf-card, #fff)', borderRadius: 12, padding: 20,
    border: '1px solid var(--surf-border, #e2e8f0)', cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--surf-shadow-lg, 0 4px 12px rgba(0,0,0,0.08))' },
  },
  cardIcon: {
    width: 44, height: 44, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center', marginBottom: 12,
  },
  filterRow: {
    display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 24px', background: 'var(--surf-card, #fff)', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)',
  },
  chip: {
    fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  table: {
    '& .MuiTableCell-head': {
      fontSize: '0.68rem', fontWeight: 700, color: 'var(--surf-muted, #64748b)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      background: 'var(--surf-head-bg, #f8fafc)', borderBottom: '2px solid var(--surf-border, #e2e8f0)',
      padding: '8px 12px',
    },
    '& .MuiTableCell-body': {
      fontSize: '0.78rem', padding: '10px 12px', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)',
      color: 'var(--surf-text, inherit)',
    },
  },
}));

const REPORT_TYPES = [
  { key: 'routes', icon: AssessmentIcon, color: '#3b82f6', bgColor: '#dbeafe',
    titlePt: 'Relatório de Rotas', titleEn: 'Routes Report',
    descPt: 'Lista de rotas com distância, duração, custos e estado',
    descEn: 'Route list with distance, duration, costs and status',
    category: 'routes' },
  { key: 'drivers', icon: PersonIcon, color: '#8b5cf6', bgColor: '#ede9fe',
    titlePt: 'Relatório de Motoristas', titleEn: 'Drivers Report',
    descPt: 'Actividade por motorista: km, rotas, horas, custos',
    descEn: 'Activity per driver: km, routes, hours, costs',
    category: 'drivers' },
  { key: 'vehicles', icon: DirectionsCarIcon, color: '#f59e0b', bgColor: '#fef3c7',
    titlePt: 'Relatório de Veículos', titleEn: 'Vehicles Report',
    descPt: 'Utilização por veículo: km, viagens, custos combustível',
    descEn: 'Vehicle usage: km, trips, fuel costs',
    category: 'vehicles' },
  { key: 'utilization', icon: DirectionsCarIcon, color: '#0ea5e9', bgColor: '#e0f2fe',
    titlePt: 'Taxa de Utilização', titleEn: 'Utilization Rate',
    descPt: 'Por viatura: dias activos, rotas, distância e % de utilização',
    descEn: 'Per vehicle: active days, routes, distance and utilization %',
    category: 'vehicles' },
  { key: 'costs', icon: AttachMoneyIcon, color: '#22c55e', bgColor: '#dcfce7',
    titlePt: 'Relatório de Custos', titleEn: 'Costs Report',
    descPt: 'Análise mensal: combustível, portagens, subsídios, total',
    descEn: 'Monthly analysis: fuel, tolls, subsidies, total',
    category: 'costs' },
  { key: 'compliance', icon: GavelIcon, color: '#ef4444', bgColor: '#fee2e2',
    titlePt: 'Conformidade EU 561/2006', titleEn: 'EU 561/2006 Compliance',
    descPt: 'Tempos de condução, pausas, infrações por motorista',
    descEn: 'Driving times, breaks, violations per driver',
    category: 'compliance' },
  { key: 'divergences', icon: CompareArrowsIcon, color: '#e11d48', bgColor: '#ffe4e6',
    titlePt: 'Relatório de Divergências', titleEn: 'Divergences Report',
    descPt: 'Combustível: litros carregados vs entregues, por período/veículo/motorista',
    descEn: 'Fuel: loaded vs delivered litres, by period/vehicle/driver',
    category: 'orders' },
  { key: 'litresByStation', icon: LocalGasStationIcon, color: '#f97316', bgColor: '#ffedd5',
    titlePt: 'Litros por Posto', titleEn: 'Litres by Station',
    descPt: 'Combustível: litros carregados por terminal (enchimento) e entregues por posto',
    descEn: 'Fuel: litres loaded by terminal and delivered by station',
    category: 'orders' },
  { key: 'orders', icon: AssignmentIcon, color: '#06b6d4', bgColor: '#cffafe',
    titlePt: 'Relatório de Ordens de Transporte', titleEn: 'Transport Orders Report',
    descPt: 'Estado das OTs: pendentes, em trânsito, entregues',
    descEn: 'OT status: pending, in transit, delivered',
    category: 'orders' },
  { key: 'daily', icon: TodayIcon, color: '#0ea5e9', bgColor: '#e0f2fe',
    titlePt: 'Resumo Diário', titleEn: 'Daily Summary',
    descPt: 'Por viatura/motorista: km, partida, fim, OTs do dia',
    descEn: 'Per vehicle/driver: km, start, end, OTs of the day',
    category: 'operations' },
  { key: 'serviceTimes', icon: BuildIcon, color: '#f59e0b', bgColor: '#fef3c7',
    titlePt: 'Tempos de Serviço (Assistência)', titleEn: 'Service Times (Assistance)',
    descPt: 'Tempo de espera vs facturável por OT de assistência (Fase 1A)',
    descEn: 'Wait time vs billable time per assistance OT (Phase 1A)',
    category: 'operations' },
];

const CATEGORIES = [
  { key: 'all', labelPt: 'Todos', labelEn: 'All' },
  { key: 'operations', labelPt: 'Operações', labelEn: 'Operations' },
  { key: 'routes', labelPt: 'Rotas', labelEn: 'Routes' },
  { key: 'drivers', labelPt: 'Motoristas', labelEn: 'Drivers' },
  { key: 'vehicles', labelPt: 'Veículos', labelEn: 'Vehicles' },
  { key: 'costs', labelPt: 'Custos', labelEn: 'Costs' },
  { key: 'compliance', labelPt: 'Conformidade', labelEn: 'Compliance' },
];

function ReportsView() {
  const classes = useStyles();
  // Novo UI (identidade ecooGPS + temas) só em Claro/Escuro; Clássico fica como
  // estava (sem data-planner-theme → os var(--surf-*) usam o fallback original).
  const theme = usePlannerTheme();
  const newUI = usePlannerToolbarTheme() !== 'classic';
  const surfTheme = newUI ? theme : undefined;
  const grad = brandGradient(theme);
  const { token, clientSettings, modules, clientExternalId } = useContext(AuthContext);
  // Perfil de combustível (Move Express) → Veículos e Ordens de Transporte
  // exportam com a folha detalhada (FUEL_ORDER_COLUMNS/FUEL_VEHICLE_COLUMNS).
  const isFuelProfile = isMoveExpressTenant(clientExternalId, clientSettings);
  // Cada relatório tem o seu sub-módulo `report_<key>`, filho de "Relatórios".
  // Fail-open quando não há lista de módulos, igual ao ModuleGate.
  const hasReport = (key) => !modules || modules.length === 0 || modules.includes(`report_${key}`);
  const dispatch = useDispatch();
  const { lang } = useSelector(selectUI);
  const pt = lang === 'pt';
  // Símbolo da moeda das preferências do cliente (Kz p/ Angola, € p/ Europa…).
  const cur = getCurrencySymbol(clientSettings?.defaultCurrency || 'EUR');
  // Move Express (Angola) só faz OT de combustível — esconde relatórios que
  // não se aplicam (Conformidade EU 561 e Tempos de Serviço/Assistência).
  // NOTA: a variante deixou de mandar nos relatórios — quem decide agora é o
  // Perfil, via os sub-módulos report_<key>. (Era aqui o HIDDEN_FOR_MOVE_EXPRESS.)

  const [category, setCategory] = useState('all');
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Filtros extra do Relatório de Divergências (por veículo / motorista).
  const [divVehicle, setDivVehicle] = useState('');
  const [divDriver, setDivDriver] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  // Cronologia + km reais por troço da OT de combustível (detalhe da rota).
  const [routeLegs, setRouteLegs] = useState(null);
  // Daily report uses a single-day picker (defaults to today).
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Cada relatório é um sub-módulo de "Relatórios" (report_<key>) → é o
  // operador que decide quais cada cliente vê, no Perfil. Isto substituiu o
  // HIDDEN_FOR_MOVE_EXPRESS, que era hardcode da variante.
  const availableReports = REPORT_TYPES.filter(r => hasReport(r.key));
  const filteredReports = category === 'all'
    ? availableReports
    : availableReports.filter(r => r.category === category);

  const fetchReport = useCallback(async (type) => {
    if (!token) return;
    setLoading(true);
    setReportData(null);

    const headers = { Authorization: `Bearer ${token}` };
    const dateParams = (dateFrom || dateTo)
      ? `&dateFrom=${dateFrom || ''}&dateTo=${dateTo || ''}`
      : '';

    try {
      let data;
      switch (type) {
        case 'routes':
          const rRes = await fetch(`/planner-api/routes?page=1&limit=100&sortBy=created_at&sortDir=desc${dateParams}`, { headers });
          const rJson = await rRes.json();
          data = rJson.data || rJson;
          break;
        case 'drivers':
          const dRes = await fetch('/planner-api/stats/costs-by-driver', { headers });
          data = await dRes.json();
          break;
        case 'vehicles':
          const vRes = await fetch('/planner-api/stats/costs-by-vehicle', { headers });
          data = await vRes.json();
          break;
        case 'utilization':
          const utRes = await fetch(`/planner-api/stats/utilization?${dateParams.replace(/^&/, '')}`, { headers });
          data = await utRes.json();
          break;
        case 'divergences': {
          const vq = divVehicle ? `&vehicle=${encodeURIComponent(divVehicle)}` : '';
          const dq = divDriver ? `&driver=${encodeURIComponent(divDriver)}` : '';
          const dvRes = await fetch(`/planner-api/stats/divergences?_=1${dateParams}${vq}${dq}`, { headers });
          data = await dvRes.json();
          break;
        }
        case 'litresByStation': {
          const lsRes = await fetch(`/planner-api/stats/litres-by-station?_=1${dateParams}`, { headers });
          data = await lsRes.json();
          break;
        }
        case 'costs':
          const cRes = await fetch(`/planner-api/stats/costs?months=12${dateParams}`, { headers });
          data = await cRes.json();
          break;
        case 'compliance':
          const compRes = await fetch(`/planner-api/routes?page=1&limit=100&status=completed${dateParams}`, { headers });
          const compJson = await compRes.json();
          data = (compJson.data || compJson).map(r => ({
            name: r.name,
            distance: r.distance ?? r.totalDistanceM,
            duration: r.duration ?? r.totalDurationS,
            stops: r.routeDataSummary?.stopsCount || 0,
            status: r.status,
          }));
          break;
        case 'orders':
          const oRes = await fetch(`/planner-api/transport-orders?page=1&limit=100${dateParams}`, { headers });
          const oJson = await oRes.json();
          data = oJson.data || [];
          break;
        case 'daily': {
          const dailyRes = await fetch(`/planner-api/stats/daily-summary?date=${dailyDate}`, { headers });
          data = await dailyRes.json();
          break;
        }
        case 'serviceTimes': {
          // Puxa todas as OTs no período (a filtragem por assistência
          // sai naturalmente do filter abaixo — só OTs com
          // serviceStartedAt sobrevivem, e isso só existe em
          // assistência). O endpoint já inclui o assistanceReport
          // (line 409 transport-orders.routes.js). Limit 500 cobre um
          // mês típico sem precisar de paginação no operador.
          const stRes = await fetch(
            `/planner-api/transport-orders?page=1&limit=500${dateParams}`,
            { headers },
          );
          const stJson = await stRes.json();
          const arr = stJson.data || stJson || [];
          data = arr
            .filter(o => o.assistanceReport?.serviceStartedAt)
            .map(o => {
              const r = o.assistanceReport;
              const arrived = r.arrivedAt ? new Date(r.arrivedAt).getTime() : null;
              const started = new Date(r.serviceStartedAt).getTime();
              const completed = r.completedAt ? new Date(r.completedAt).getTime() : null;
              const waitMin = (arrived && started > arrived)
                ? Math.round((started - arrived) / 60000) : null;
              const serviceMin = completed
                ? Math.round((completed - started) / 60000)
                : (Number.isFinite(r.actualDurationMin) ? r.actualDurationMin : null);
              const totalOnSite = arrived && completed
                ? Math.round((completed - arrived) / 60000) : null;
              return {
                orderId: o.id,
                orderNumber: o.orderNumber,
                customer: o.deliveryName || o.pickupName || '—',
                driver: o.driverName || '—',
                vehicle: o.vehiclePlate || '—',
                arrivedAt: r.arrivedAt || null,
                serviceStartedAt: r.serviceStartedAt,
                completedAt: r.completedAt || null,
                waitMin,
                serviceMin,
                totalOnSite,
                outcome: r.outcome || null,
                inProgress: !completed,
              };
            })
            .sort((a, b) => new Date(b.serviceStartedAt) - new Date(a.serviceStartedAt));
          break;
        }
        default:
          data = [];
      }
      setReportData(data);
    } catch (err) {
      console.error('Report fetch error:', err);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, dailyDate, divVehicle, divDriver]);

  function handleOpenReport(type) {
    setActiveReport(type);
    fetchReport(type);
  }

  // Ao abrir o detalhe de uma rota de combustível, busca a cronologia com
  // km/tempo reais por troço (mesmo endpoint do painel da OT).
  useEffect(() => {
    const ot = selectedRoute?.otRefs?.[0];
    const isFuel = ot?.serviceType === 'fuel';
    if (!selectedRoute || !isFuel || !ot?.id || !token) { setRouteLegs(null); return; }
    let cancelled = false;
    setRouteLegs({ loading: true });
    fetch(`/planner-api/transport-orders/${ot.id}/fuel-leg-metrics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setRouteLegs(d || null); })
      .catch(() => { if (!cancelled) setRouteLegs(null); });
    return () => { cancelled = true; };
  }, [selectedRoute, token]);

  // Linhas prontas a exportar (achata os relatórios com forma { ... }).
  function exportRowsFor(key) {
    if (key === 'divergences') return reportData?.rows || [];
    if (key === 'litresByStation') return [
      ...(reportData?.loading || []).map(r => ({ tipo: 'enchimento', ...r })),
      ...(reportData?.delivery || []).map(r => ({ tipo: 'entrega', ...r })),
    ];
    return Array.isArray(reportData) ? reportData : (reportData ? [reportData] : []);
  }

  // Colunas [cabeçalho PT/EN, acessor] por relatório — cabeçalhos legíveis e
  // VALORES traduzidos (estado, fase, tipo…). Usadas na exportação Excel e PDF
  // para nenhum campo sair em inglês (pedido Move Express). Devolve null quando
  // não há definição (cai então para a exportação genérica, ainda com título).
  function exportColumnsFor(key) {
    const km = (m) => (m != null ? Number((m / 1000).toFixed(1)) : '');
    const durS = (s) => (s != null ? `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m` : '');
    const dt = (d) => (d ? new Date(d).toLocaleDateString('pt-PT') : '');
    const ori = (r) => (typeof r.origin === 'string' ? r.origin : r.origin?.name || '');
    const des = (r) => (typeof r.destination === 'string' ? r.destination : r.destination?.name || '');
    const cols = {
      routes: [
        [pt ? 'Nome' : 'Name', r => r.name || ''],
        [pt ? 'Origem' : 'Origin', r => ori(r)],
        [pt ? 'Carregamento' : 'Loading', r => routeLoading(r)],
        [pt ? 'Destino' : 'Destination', r => des(r)],
        [pt ? 'Distância (km)' : 'Distance (km)', r => km(routeDistM(r))],
        [pt ? 'Duração' : 'Duration', r => durS(routeDurS(r))],
        [pt ? 'Estado' : 'Status', r => routeStatusLabel(r.status)],
        [pt ? 'Data' : 'Date', r => dt(r.createdAt)],
        [pt ? 'Veículo' : 'Vehicle', r => routeVehicle(r)],
        [pt ? 'Cisterna' : 'Cistern', r => routeCistern(r)],
        [pt ? 'Motorista' : 'Driver', r => routeDriver(r)],
      ],
      drivers: [
        [pt ? 'Motorista' : 'Driver', d => d.name || ''],
        [pt ? 'Nº Rotas' : 'Routes', d => d.routeCount ?? 0],
        [pt ? 'Distância Total (km)' : 'Total Distance (km)', d => km(d.totalDistance)],
        [pt ? 'Média km/Rota' : 'Avg km/Route', d => km(d.avgDistance)],
      ],
      vehicles: [
        [pt ? 'Matrícula' : 'Plate', v => v.plate || ''],
        [pt ? 'Tipo' : 'Type', v => v.type || ''],
        [pt ? 'Nº Rotas' : 'Routes', v => v.routeCount ?? 0],
        [pt ? 'Distância Total (km)' : 'Total Distance (km)', v => km(v.totalDistance)],
      ],
      utilization: [
        [pt ? 'Matrícula' : 'Plate', v => v.plate || ''],
        [pt ? 'Dias activos' : 'Active days', v => v.activeDays ?? 0],
        [pt ? 'Dias do período' : 'Period days', v => v.periodDays ?? 0],
        [pt ? 'Nº Rotas' : 'Routes', v => v.routeCount ?? 0],
        [pt ? 'Distância (km)' : 'Distance (km)', v => km(v.totalDistance)],
        [pt ? 'Taxa de utilização (%)' : 'Utilization (%)', v => v.utilizationPct ?? 0],
      ],
      costs: [
        [pt ? 'Mês' : 'Month', r => r.month || ''],
        [pt ? 'Nº Rotas' : 'Routes', r => r.routeCount ?? 0],
        [pt ? 'Distância (km)' : 'Distance (km)', r => km(r.totalDistance)],
        [`${pt ? 'Combustível' : 'Fuel'} (${cur})`, r => Number(r.fuelCost || 0).toFixed(2)],
        [`${pt ? 'Portagens' : 'Tolls'} (${cur})`, r => Number(r.tollCost || 0).toFixed(2)],
        [`${pt ? 'Total' : 'Total'} (${cur})`, r => (Number(r.fuelCost || 0) + Number(r.tollCost || 0)).toFixed(2)],
      ],
      compliance: [
        [pt ? 'Rota' : 'Route', r => r.name || ''],
        [pt ? 'Distância (km)' : 'Distance (km)', r => km(r.distance)],
        [pt ? 'Duração' : 'Duration', r => durS(r.duration)],
        [pt ? 'Paragens' : 'Stops', r => r.stops || 0],
        [pt ? 'Estado' : 'Status', r => routeStatusLabel(r.status)],
      ],
      divergences: [
        [pt ? 'Data' : 'Date', r => dt(r.date)],
        [pt ? 'OT' : 'Order', r => r.orderNumber || ''],
        [pt ? 'Veículo' : 'Vehicle', r => r.vehiclePlate || ''],
        [pt ? 'Motorista' : 'Driver', r => r.driverName || ''],
        [pt ? 'Carregado (L)' : 'Loaded (L)', r => xNum(r.loaded)],
        [pt ? 'Entregue (L)' : 'Delivered (L)', r => xNum(r.delivered)],
        // Convenção da app: divergência = entregue − carregado (negamos o cru).
        [pt ? 'Divergência (L)' : 'Divergence (L)', r => (r.divergence == null ? '' : -Number(r.divergence))],
        [pt ? 'Motivo' : 'Reason', r => r.reason || ''],
      ],
      litresByStation: [
        [pt ? 'Tipo' : 'Type', r => (r.tipo === 'enchimento' ? (pt ? 'Enchimento' : 'Loading') : (pt ? 'Entrega' : 'Delivery'))],
        [pt ? 'Posto / Terminal' : 'Station / Terminal', r => r.station || ''],
        [pt ? 'OTs' : 'Orders', r => r.count ?? 0],
        [pt ? 'Litros' : 'Litres', r => xNum(r.litres)],
        // % relativo ao total da respectiva secção (enchimento vs entrega).
        ['%', r => { const tot = r.tipo === 'enchimento' ? reportData?.totals?.loadingLitres : reportData?.totals?.deliveryLitres; return tot > 0 ? `${Math.round((Number(r.litres || 0) / tot) * 100)}%` : '0%'; }],
      ],
      // orders básico — só usado por tenants NÃO combustível (o perfil fuel usa
      // FUEL_ORDER_COLUMNS, mais detalhado). Espelha a tabela do ecrã.
      orders: [
        [pt ? 'Nº Ordem' : 'Order #', o => o.orderNumber || ''],
        [pt ? 'Recolha' : 'Pickup', o => o.pickupName || ''],
        [pt ? 'Entrega' : 'Delivery', o => o.deliveryName || ''],
        [pt ? 'Carga' : 'Cargo', o => o.cargoDescription || ''],
        [pt ? 'Estado' : 'Status', o => otStatusText(o.status, pt)],
        [pt ? 'Tempo serviço (min)' : 'Service time (min)', o => xNum(o.assistanceReport?.actualDurationMin)],
        [pt ? 'Preço' : 'Price', o => xNum(o.agreedPrice)],
        [pt ? 'Data' : 'Date', o => dt(o.createdAt)],
      ],
      daily: [
        [pt ? 'Dia' : 'Day', r => r.date || ''],
        [pt ? 'Matrícula' : 'Plate', r => r.vehiclePlate || ''],
        [pt ? 'Condutor' : 'Driver', r => r.driverName || ''],
        [pt ? 'Início (local)' : 'Start (location)', r => r.startName || ''],
        [pt ? 'Fim (local)' : 'End (location)', r => r.endName || ''],
        [pt ? 'Km' : 'Km', r => (r.distanceKm != null ? Number(r.distanceKm.toFixed(1)) : '')],
        [pt ? 'OTs do dia' : 'OTs', r => r.totalOTs ?? 0],
        [pt ? 'Executadas' : 'Executed', r => r.deliveredOTs ?? 0],
        [pt ? 'Não executadas' : 'Not executed', r => r.notDeliveredOTs ?? 0],
      ],
      serviceTimes: [
        [pt ? 'OT' : 'OT', r => r.orderNumber || `OT-${r.orderId}`],
        [pt ? 'Cliente final' : 'Customer', r => r.customer || ''],
        [pt ? 'Motorista' : 'Driver', r => r.driver || ''],
        [pt ? 'Viatura' : 'Vehicle', r => r.vehicle || ''],
        [pt ? 'Chegou' : 'Arrived', r => xDateTime(r.arrivedAt)],
        [pt ? 'Início serviço' : 'Service start', r => xDateTime(r.serviceStartedAt)],
        [pt ? 'Fim' : 'End', r => (r.inProgress ? (pt ? 'em curso' : 'in progress') : xDateTime(r.completedAt))],
        [pt ? 'Espera (min)' : 'Wait (min)', r => xNum(r.waitMin)],
        [pt ? 'Serviço (min)' : 'Service (min)', r => xNum(r.serviceMin)],
        [pt ? 'Total no local (min)' : 'Total on-site (min)', r => xNum(r.totalOnSite)],
      ],
    };
    return cols[key] || null;
  }

  function handleExportExcel() {
    if (!reportData || !activeReport) return;
    const rt = REPORT_TYPES.find(r => r.key === activeReport);
    const title = pt ? rt?.titlePt : rt?.titleEn;
    // Perfil combustível: Veículos e Ordens de Transporte saem com a folha
    // detalhada (uma coluna por campo da BD). Os restantes usam as colunas PT
    // de exportColumnsFor. Ambos os caminhos levam agora o NOME do relatório e a
    // data no topo (antes o Excel saía sem nome — pedido Move Express).
    const fuelCols = (isFuelProfile && (activeReport === 'orders' || activeReport === 'vehicles'))
      ? (activeReport === 'orders' ? FUEL_ORDER_COLUMNS : FUEL_VEHICLE_COLUMNS)
      : null;
    const cols = fuelCols || exportColumnsFor(activeReport);
    const rows = exportRowsFor(activeReport);
    import('xlsx').then(XLSX => {
      let ws;
      if (cols) {
        const header = cols.map(c => c[0]);
        const body = rows.map(r => cols.map(c => c[1](r)));
        const { aoa, widths, merges } = buildTitledSheet(title || 'Relatório', header, body);
        ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = widths;
        if (merges.length) ws['!merges'] = merges;
      } else {
        // Fallback genérico (relatório sem colunas definidas) — ainda com título.
        ws = XLSX.utils.json_to_sheet(rows, { origin: 3 });
        XLSX.utils.sheet_add_aoa(ws, [[title || 'Relatório'], [`Gerado em ${new Date().toLocaleString('pt-PT')}`]], { origin: 'A1' });
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(title));
      XLSX.writeFile(wb, `relatorio_${activeReport}_${new Date().toISOString().slice(0,10)}.xlsx`);
    });
  }

  function handlePrint() {
    window.print();
  }

  function handleGeneratePDF() {
    if (!reportData || !activeReport) return;
    const rt = REPORT_TYPES.find(r => r.key === activeReport);
    const title = pt ? rt?.titlePt : rt?.titleEn;
    // O PDF espelha as COLUNAS DO ECRÃ (exportColumnsFor) — não a folha Excel
    // detalhada. Tabela profissional (jspdf-autotable), na língua do planner.
    const cols = exportColumnsFor(activeReport);
    const rows = exportRowsFor(activeReport);
    Promise.all([import('jspdf'), import('jspdf-autotable')]).then(([{ default: jsPDF }, auto]) => {
      const autoTable = auto.default || auto;
      const landscape = cols && cols.length > 6;
      const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      // Cabeçalho — título (PT/EN) + data + nº de registos.
      doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(21, 128, 61);
      doc.text(title || 'Relatório', 40, 42);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(
        `${pt ? 'Gerado em' : 'Generated'} ${new Date().toLocaleString(pt ? 'pt-PT' : 'en-GB')}  ·  ${rows.length} ${pt ? 'registos' : 'records'}`,
        40, 58,
      );
      if (cols && cols.length) {
        // Resumo/totais do topo do ecrã (Divergências, Litros por Posto) — no PDF
        // saíam a faltar. Reproduz as caixas-resumo como um mini-quadro.
        const nLoc = pt ? 'pt-PT' : 'en-GB';
        const fmtL = (n) => `${Number(n || 0).toLocaleString(nLoc)} L`;
        const fmtDiv = (n) => { if (n == null) return '—'; const v = Math.abs(Number(n)) < 0.001 ? 0 : Number(n); return `${v > 0 ? '+' : ''}${v.toLocaleString(nLoc)} L`; };
        let summary = null;
        if (activeReport === 'divergences') {
          const t = reportData?.totals || {};
          const net = t.divergence == null ? null : -Number(t.divergence);
          summary = [
            [pt ? 'OTs' : 'Orders', String(t.count ?? 0)],
            [pt ? 'Total carregado' : 'Total loaded', fmtL(t.loaded)],
            [pt ? 'Total entregue' : 'Total delivered', fmtL(t.delivered)],
            [pt ? 'Divergência total' : 'Total divergence', fmtDiv(net)],
            [pt ? 'Com divergência' : 'With divergence', String(t.withDivergence ?? 0)],
          ];
        } else if (activeReport === 'litresByStation') {
          const t = reportData?.totals || {};
          summary = [
            [pt ? 'Enchimento — total (terminais)' : 'Loading — total (terminals)', fmtL(t.loadingLitres)],
            [pt ? 'Entrega — total (postos)' : 'Delivery — total (stations)', fmtL(t.deliveryLitres)],
          ];
        }
        let startY = 72;
        if (summary && summary.length) {
          autoTable(doc, {
            startY,
            head: [[pt ? 'Resumo' : 'Summary', '']],
            body: summary.map(s => [sanitizePdfText(s[0]), sanitizePdfText(s[1])]),
            theme: 'plain',
            styles: { fontSize: 8.5, cellPadding: 3, textColor: [30, 41, 59] },
            headStyles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'bold', fontSize: 8.5 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 200 }, 1: { textColor: [21, 128, 61], fontStyle: 'bold' } },
            margin: { left: 40, right: 40 },
            tableWidth: 360,
          });
          startY = (doc.lastAutoTable?.finalY || startY) + 16;
        }
        autoTable(doc, {
          startY,
          head: [cols.map(c => sanitizePdfText(c[0]))],
          body: rows.map(r => cols.map(c => sanitizePdfText(c[1](r)))),
          theme: 'grid',
          styles: { fontSize: 7.5, cellPadding: 4, overflow: 'linebreak', textColor: [30, 41, 59], lineColor: [226, 232, 240], lineWidth: 0.5 },
          headStyles: { fillColor: [21, 128, 61], textColor: 255, fontStyle: 'bold', fontSize: 7.5, halign: 'left' },
          alternateRowStyles: { fillColor: [240, 253, 244] },
          margin: { left: 40, right: 40, bottom: 40 },
        });
      } else {
        doc.setFontSize(10); doc.setTextColor(100, 116, 139);
        doc.text(pt ? 'Sem dados para apresentar.' : 'No data to display.', 40, 90);
      }
      // Rodapé em todas as páginas — marca + paginação (X de Y).
      const total = doc.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(148, 163, 184);
        doc.text(`EcooGPS Planner · ${title || ''}`, 40, pageH - 20);
        const pageStr = `${pt ? 'Página' : 'Page'} ${i}/${total}`;
        doc.text(pageStr, pageW - 40 - doc.getTextWidth(pageStr), pageH - 20);
      }
      doc.save(`relatorio_${activeReport}_${new Date().toISOString().slice(0, 10)}.pdf`);
    });
  }

  function handleRouteDetailPDF(route) {
    import('jspdf').then(({ default: jsPDF }) => {
      const doc = new jsPDF();
      const name = route.name || 'Rota';
      const origin = typeof route.origin === 'string' ? route.origin : route.origin?.name || '—';
      const dest = typeof route.destination === 'string' ? route.destination : route.destination?.name || '—';
      doc.setFontSize(14); doc.text(`Relatório de Rota: ${name}`, 14, 20);
      doc.setFontSize(8); doc.text(`Gerado: ${new Date().toLocaleString('pt-PT')}`, 14, 28);
      let y = 38;
      doc.setFontSize(10);
      const lines = [
        `Origem: ${origin}`,
        `Carregamento: ${routeLoading(route)}`,
        `Destino: ${dest}`,
        `Distância: ${routeDistM(route) ? (routeDistM(route)/1000).toFixed(1)+' km' : '—'}`,
        `Duração: ${routeDurS(route) ? Math.floor(routeDurS(route)/3600)+'h '+Math.floor((routeDurS(route)%3600)/60)+'min' : '—'}`,
        `Estado: ${routeStatusLabel(route.status)}`,
        `Data: ${route.createdAt ? new Date(route.createdAt).toLocaleDateString('pt-PT') : '—'}`,
        `Veículo: ${routeVehicle(route)}`,
        `Cisterna: ${routeCistern(route)}`,
        `Motorista: ${routeDriver(route)}`,
      ];
      lines.forEach(l => { doc.text(l, 14, y); y += 7; });
      doc.save(`rota_${name.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
    });
  }

  // Estado da rota traduzido (o campo cru vem em inglês do backend).
  function routeStatusLabel(status) {
    const map = {
      draft:       { pt: 'Rascunho',  en: 'Draft' },
      planned:     { pt: 'Planeada',  en: 'Planned' },
      in_progress: { pt: 'Em curso',  en: 'In progress' },
      completed:   { pt: 'Concluída', en: 'Completed' },
      cancelled:   { pt: 'Cancelada', en: 'Cancelled' },
    };
    const e = map[status];
    return e ? (pt ? e.pt : e.en) : (status || '—');
  }

  // A rota devolve totalDistanceM/totalDurationS (não distance/duration) —
  // fallback para as colunas mostrarem valores em vez de "—".
  function routeDistM(x) { return x?.distance ?? x?.totalDistanceM ?? null; }
  function routeDurS(x) { return x?.duration ?? x?.totalDurationS ?? null; }

  // Derivados para o relatório de rotas (a partir da 1ª OT da rota + rota).
  function routeLoading(r) { return (r.otRefs || [])[0]?.loadingName || '—'; }
  function routeVehicle(r) { return r.vehicle?.plate || r.vehiclePlate || '—'; }
  function routeDriver(r) { return r.driver?.name || r.driverName || '—'; }
  function routeCistern(r) {
    const ot = (r.otRefs || [])[0];
    if (!ot) return '—';
    if (Array.isArray(ot.cisterns) && ot.cisterns.length) {
      return ot.cisterns.map(c => `${c.plate || '?'}${c.capacityM3 != null ? ' ' + c.capacityM3 + ' m³' : ''}`).join(' + ');
    }
    if (ot.cisternPlate) return `${ot.cisternPlate}${ot.cisternCapacityM3 != null ? ' ' + ot.cisternCapacityM3 + ' m³' : ''}`;
    return '—';
  }

  // ── Route detail sub-view ──
  if (selectedRoute) {
    const r = selectedRoute;
    const origin = typeof r.origin === 'string' ? r.origin : r.origin?.name || '—';
    const dest = typeof r.destination === 'string' ? r.destination : r.destination?.name || '—';
    const _dM = routeDistM(r), _dS = routeDurS(r);
    const distKm = _dM ? (_dM / 1000).toFixed(1) : '—';
    const durH = _dS ? Math.floor(_dS / 3600) : 0;
    const durM = _dS ? Math.floor((_dS % 3600) / 60) : 0;
    return (
      <Box className={classes.root} data-planner-theme={surfTheme}>
        <Box className={classes.header}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconButton size="small" onClick={() => setSelectedRoute(null)}>
              <ArrowBackIcon style={{ fontSize: 20 }} />
            </IconButton>
            {newUI ? (
              <Box style={{ width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: grad, flexShrink: 0, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' }}>
                <AssessmentIcon style={{ fontSize: 19, color: ON_BRAND }} />
              </Box>
            ) : <AssessmentIcon style={{ fontSize: 20, color: '#3b82f6' }} />}
            <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--surf-strong, #1e293b)' }}>
              {r.name || pt ? 'Detalhe da Rota' : 'Route Detail'}
            </Typography>
          </Box>
          <Box style={{ display: 'flex', gap: 8 }}>
            <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon style={{ fontSize: 14 }} />}
              onClick={() => handleRouteDetailPDF(r)}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#ef4444', color: '#fff' }}>
              PDF
            </Button>
            <Button size="small" variant="outlined" startIcon={<PrintIcon style={{ fontSize: 14 }} />}
              onClick={handlePrint}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8 }}>
              {pt ? 'Imprimir' : 'Print'}
            </Button>
          </Box>
        </Box>
        <Box className={classes.content}>
          {/* Summary cards */}
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: pt ? 'Distância' : 'Distance', value: `${distKm} km`, color: '#3b82f6' },
              { label: pt ? 'Duração' : 'Duration', value: `${durH}h ${durM}min`, color: '#8b5cf6' },
              { label: pt ? 'Estado' : 'Status', value: routeStatusLabel(r.status), color: '#22c55e' },
              { label: pt ? 'Paragens' : 'Stops', value: String(r.routeDataSummary?.stopsCount || 0), color: '#f59e0b' },
            ].map((card, i) => (
              <Paper key={i} style={{ padding: 16, borderRadius: 12, borderLeft: `4px solid ${card.color}` }}>
                <Typography style={{ fontSize: '0.65rem', color: 'var(--surf-muted, #64748b)', textTransform: 'uppercase', fontWeight: 600 }}>{card.label}</Typography>
                <Typography style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--surf-strong, #1e293b)' }}>{card.value}</Typography>
              </Paper>
            ))}
          </Box>
          {/* Route info */}
          <Paper style={{ padding: 20, borderRadius: 12, marginBottom: 16 }}>
            <Typography style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 12, color: 'var(--surf-strong, #1e293b)' }}>
              {pt ? 'Informação da Rota' : 'Route Information'}
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: pt ? 'Nome' : 'Name', value: r.name || '—' },
                { label: pt ? 'Origem' : 'Origin', value: origin },
                { label: pt ? 'Carregamento' : 'Loading', value: routeLoading(r) },
                { label: pt ? 'Destino' : 'Destination', value: dest },
                { label: pt ? 'Distância' : 'Distance', value: `${distKm} km` },
                { label: pt ? 'Duração' : 'Duration', value: `${durH}h ${durM}min` },
                { label: pt ? 'Estado' : 'Status', value: routeStatusLabel(r.status) },
                { label: pt ? 'Data Criação' : 'Created', value: r.createdAt ? new Date(r.createdAt).toLocaleString('pt-PT') : '—' },
                { label: pt ? 'Veículo' : 'Vehicle', value: routeVehicle(r) },
                { label: pt ? 'Cisterna' : 'Cistern', value: routeCistern(r) },
                { label: pt ? 'Motorista' : 'Driver', value: routeDriver(r) },
                { label: pt ? 'Referência' : 'Reference', value: r.cargoRef || '—' },
              ].map((item, i) => (
                <Box key={i}>
                  <Typography style={{ fontSize: '0.62rem', color: 'var(--surf-muted2, #94a3b8)', textTransform: 'uppercase', fontWeight: 600 }}>{item.label}</Typography>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--surf-strong, #1e293b)' }}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Cronologia da OT de combustível — km/tempo reais por troço. */}
          {routeLegs && !routeLegs.loading && routeLegs.legs && (() => {
            const L = routeLegs.legs;
            const steps = [
              { key: 'base_terminal', pt: 'Base → Terminal', en: 'Base → Terminal' },
              { key: 'terminal_pa',   pt: 'Terminal → PA',   en: 'Terminal → PA' },
              { key: 'pa_entrega',    pt: 'PA → Entrega',    en: 'PA → Delivery' },
              { key: 'entrega_base',  pt: 'Entrega → Base',  en: 'Delivery → Base' },
            ];
            const fmtKm = (km) => km != null ? `${Number(km).toFixed(1)} km` : '—';
            const fmtMin = (m) => m == null ? '—' : (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`);
            return (
              <Paper style={{ padding: 20, borderRadius: 12, marginBottom: 16 }}>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 4, color: 'var(--surf-strong, #1e293b)' }}>
                  ⛽ {pt ? 'Cronologia da distribuição' : 'Distribution timeline'}
                </Typography>
                <Typography style={{ fontSize: '0.66rem', color: 'var(--surf-muted2, #94a3b8)', marginBottom: 12 }}>
                  {pt ? 'Distâncias percorridas (GPS) e tempo por troço' : 'Distances travelled (GPS) and time per leg'}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{pt ? 'Troço' : 'Leg'}</TableCell>
                        <TableCell align="right">{pt ? 'Distância' : 'Distance'}</TableCell>
                        <TableCell align="right">{pt ? 'Tempo' : 'Time'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {steps.map(s => (
                        <TableRow key={s.key}>
                          <TableCell style={{ fontSize: '0.76rem' }}>{pt ? s.pt : s.en}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.76rem' }}>{fmtKm(L[s.key]?.km)}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.76rem' }}>{fmtMin(L[s.key]?.minutes)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell style={{ fontWeight: 800, fontSize: '0.78rem' }}>{pt ? 'Total' : 'Total'}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 800, fontFamily: 'Roboto Mono, monospace', color: '#16a34a' }}>{fmtKm(routeLegs.totalKm)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 800, fontFamily: 'Roboto Mono, monospace', color: '#16a34a' }}>{fmtMin(routeLegs.totalMinutes)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}
        </Box>
      </Box>
    );
  }

  // ── Card grid view ──
  if (!activeReport) {
    return (
      <Box className={classes.root} data-planner-theme={surfTheme}>
        <Box className={classes.header}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconButton size="small" onClick={() => dispatch(setActiveView('planner'))} style={{ color: 'var(--surf-muted, #64748b)' }}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
            </IconButton>
            {newUI ? (
              <Box style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: grad, flexShrink: 0, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' }}>
                <AssessmentIcon style={{ fontSize: 21, color: ON_BRAND }} />
              </Box>
            ) : <AssessmentIcon style={{ fontSize: 22, color: '#3b82f6' }} />}
            <Typography style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--surf-strong, #1e293b)' }}>
              {pt ? 'Relatórios' : 'Reports'}
            </Typography>
            <Chip label={`${REPORT_TYPES.length} ${pt ? 'tipos' : 'types'}`} size="small"
              style={{ height: 22, fontSize: '0.65rem', fontWeight: 600, background: '#e0e7ff', color: '#4338ca' }} />
          </Box>
        </Box>

        {/* Category filter — DERIVA dos relatórios disponíveis: uma categoria
            sem relatórios ligados não faz sentido aparecer. Antes era hardcode
            da variante ("esconder Conformidade à Move Express"). */}
        <Box className={classes.filterRow}>
          {CATEGORIES.filter(c => c.key === 'all' || availableReports.some(r => r.category === c.key)).map(c => (
            <Chip key={c.key} label={pt ? c.labelPt : c.labelEn}
              className={classes.chip}
              onClick={() => setCategory(c.key)}
              style={{
                background: category === c.key ? '#3b82f6' : '#f1f5f9',
                color: category === c.key ? '#fff' : '#64748b',
              }} />
          ))}
        </Box>

        <Box className={classes.content}>
          <Box className={classes.grid}>
            {filteredReports.map(r => (
              <Box key={r.key} className={classes.card} onClick={() => handleOpenReport(r.key)}>
                <Box className={classes.cardIcon} style={{ background: r.bgColor }}>
                  <r.icon style={{ fontSize: 22, color: r.color }} />
                </Box>
                <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--surf-strong, #1e293b)', marginBottom: 4 }}>
                  {pt ? r.titlePt : r.titleEn}
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: 'var(--surf-muted, #64748b)', lineHeight: 1.4 }}>
                  {pt ? r.descPt : r.descEn}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Report detail view ──
  const reportType = REPORT_TYPES.find(r => r.key === activeReport);

  return (
    <Box className={classes.root} data-planner-theme={surfTheme}>
      <Box className={classes.header}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconButton size="small" onClick={() => { setActiveReport(null); setReportData(null); }}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
          {reportType && <reportType.icon style={{ fontSize: 20, color: reportType.color }} />}
          <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--surf-strong, #1e293b)' }}>
            {pt ? reportType?.titlePt : reportType?.titleEn}
          </Typography>
          {reportData && (
            <Chip label={`${Array.isArray(reportData) ? reportData.length : (reportData?.rows?.length ?? ((reportData?.loading?.length || 0) + (reportData?.delivery?.length || 0)))} ${pt ? 'registos' : 'records'}`}
              size="small" style={{ height: 22, fontSize: '0.65rem', fontWeight: 600, background: '#e0e7ff', color: '#4338ca' }} />
          )}
        </Box>
        <Box style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Excel">
            <Button size="small" variant="outlined" startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
              onClick={handleExportExcel}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8 }}>
              Excel
            </Button>
          </Tooltip>
          <Tooltip title="PDF">
            <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon style={{ fontSize: 14 }} />}
              onClick={handleGeneratePDF}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#ef4444', color: '#fff' }}>
              PDF
            </Button>
          </Tooltip>
          <Tooltip title={pt ? 'Imprimir' : 'Print'}>
            <Button size="small" variant="outlined" startIcon={<PrintIcon style={{ fontSize: 14 }} />}
              onClick={handlePrint}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8 }}>
              {pt ? 'Imprimir' : 'Print'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Date filters — daily report uses a single-day picker, others use a range */}
      <Box className={classes.filterRow}>
        {activeReport === 'daily' ? (
          <TextField type="date" size="small" variant="outlined" label={pt ? 'Dia' : 'Day'}
            value={dailyDate} onChange={e => setDailyDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ width: 180 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
        ) : (
          <>
            <TextField type="date" size="small" variant="outlined" label={pt ? 'De' : 'From'}
              value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 160 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
            <TextField type="date" size="small" variant="outlined" label={pt ? 'Até' : 'To'}
              value={dateTo} onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 160 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
          </>
        )}
        {activeReport === 'divergences' && (
          <>
            <TextField size="small" variant="outlined" label={pt ? 'Veículo' : 'Vehicle'}
              value={divVehicle} onChange={e => setDivVehicle(e.target.value)}
              style={{ width: 150 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
            <TextField size="small" variant="outlined" label={pt ? 'Motorista' : 'Driver'}
              value={divDriver} onChange={e => setDivDriver(e.target.value)}
              style={{ width: 170 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
          </>
        )}
        <Button size="small" variant="contained"
          onClick={() => fetchReport(activeReport)}
          style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#3b82f6', color: '#fff' }}>
          {pt ? 'Filtrar' : 'Filter'}
        </Button>
      </Box>

      <Box className={classes.content}>
        {loading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !reportData ? (
          <Typography style={{ textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', padding: 60 }}>
            {pt ? 'Sem dados' : 'No data'}
          </Typography>
        ) : activeReport === 'costs' ? (
          /* Costs chart + table */
          <Box>
            <Paper style={{ borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <Typography style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 12 }}>
                {pt ? `Custos Mensais (${cur})` : `Monthly Costs (${cur})`}
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={reportData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}${cur}`} />
                  <RTooltip formatter={(v) => `${Number(v).toFixed(2)}${cur}`} />
                  <Bar dataKey="fuelCost" name={pt ? 'Combustível' : 'Fuel'} fill="#3b82f6" radius={[4,4,0,0]} />
                  <Bar dataKey="tollCost" name={pt ? 'Portagens' : 'Tolls'} fill="#f59e0b" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
            <TableContainer component={Paper} style={{ borderRadius: 12 }}>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>{pt ? 'Mês' : 'Month'}</TableCell>
                    <TableCell align="right">{pt ? 'Rotas' : 'Routes'}</TableCell>
                    <TableCell align="right">{pt ? 'Distância (km)' : 'Distance (km)'}</TableCell>
                    <TableCell align="right">{pt ? `Combustível (${cur})` : `Fuel (${cur})`}</TableCell>
                    <TableCell align="right">{pt ? `Portagens (${cur})` : `Tolls (${cur})`}</TableCell>
                    <TableCell align="right">{pt ? `Total (${cur})` : `Total (${cur})`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell style={{ fontWeight: 600 }}>{row.month}</TableCell>
                      <TableCell align="right">{row.routeCount}</TableCell>
                      <TableCell align="right">{(row.totalDistance / 1000).toFixed(0)}</TableCell>
                      <TableCell align="right">{Number(row.fuelCost || 0).toFixed(2)}{cur}</TableCell>
                      <TableCell align="right">{Number(row.tollCost || 0).toFixed(2)}{cur}</TableCell>
                      <TableCell align="right" style={{ fontWeight: 700 }}>
                        {(Number(row.fuelCost || 0) + Number(row.tollCost || 0)).toFixed(2)}{cur}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : activeReport === 'routes' ? (
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Nome' : 'Name'}</TableCell>
                  <TableCell>{pt ? 'Origem' : 'Origin'}</TableCell>
                  <TableCell>{pt ? 'Carregamento' : 'Loading'}</TableCell>
                  <TableCell>{pt ? 'Destino' : 'Destination'}</TableCell>
                  <TableCell align="right">{pt ? 'Distância' : 'Distance'}</TableCell>
                  <TableCell align="right">{pt ? 'Duração' : 'Duration'}</TableCell>
                  <TableCell>{pt ? 'Estado' : 'Status'}</TableCell>
                  <TableCell>{pt ? 'Data' : 'Date'}</TableCell>
                  <TableCell>{pt ? 'Veículo' : 'Vehicle'}</TableCell>
                  <TableCell>{pt ? 'Cisterna' : 'Cistern'}</TableCell>
                  <TableCell>{pt ? 'Motorista' : 'Driver'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((r, i) => (
                  <TableRow key={i} hover style={{ cursor: 'pointer' }} onClick={() => setSelectedRoute(r)}>
                    <TableCell style={{ fontWeight: 600 }}>{r.name || '—'}</TableCell>
                    <TableCell style={{ fontSize: '0.72rem' }}>{typeof r.origin === 'string' ? r.origin : r.origin?.name || '—'}</TableCell>
                    <TableCell style={{ fontSize: '0.72rem' }}>{routeLoading(r)}</TableCell>
                    <TableCell style={{ fontSize: '0.72rem' }}>{typeof r.destination === 'string' ? r.destination : r.destination?.name || '—'}</TableCell>
                    <TableCell align="right">{routeDistM(r) ? `${(routeDistM(r)/1000).toFixed(1)} km` : '—'}</TableCell>
                    <TableCell align="right">{routeDurS(r) ? `${Math.floor(routeDurS(r)/3600)}h${Math.floor((routeDurS(r)%3600)/60)}m` : '—'}</TableCell>
                    <TableCell>
                      <Chip label={routeStatusLabel(r.status)} size="small"
                        style={{ height: 20, fontSize: '0.6rem', fontWeight: 700,
                          background: r.status === 'completed' ? '#dcfce7' : r.status === 'planned' ? '#dbeafe' : '#f1f5f9',
                          color: r.status === 'completed' ? '#16a34a' : r.status === 'planned' ? '#2563eb' : '#64748b' }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '0.72rem' }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('pt-PT') : '—'}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.72rem', fontFamily: 'Roboto Mono, monospace', fontWeight: 600 }}>{routeVehicle(r)}</TableCell>
                    <TableCell style={{ fontSize: '0.72rem', fontFamily: 'Roboto Mono, monospace' }}>{routeCistern(r)}</TableCell>
                    <TableCell style={{ fontSize: '0.72rem' }}>{routeDriver(r)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeReport === 'drivers' ? (
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Motorista' : 'Driver'}</TableCell>
                  <TableCell align="right">{pt ? 'Nº Rotas' : 'Routes'}</TableCell>
                  <TableCell align="right">{pt ? 'Distância Total' : 'Total Distance'}</TableCell>
                  <TableCell align="right">{pt ? 'Média km/Rota' : 'Avg km/Route'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((d, i) => (
                  <TableRow key={i} hover>
                    <TableCell style={{ fontWeight: 600 }}>{d.name || '—'}</TableCell>
                    <TableCell align="right">{d.routeCount}</TableCell>
                    <TableCell align="right">{(d.totalDistance / 1000).toFixed(0)} km</TableCell>
                    <TableCell align="right">{(d.avgDistance / 1000).toFixed(0)} km</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeReport === 'vehicles' ? (
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Matrícula' : 'Plate'}</TableCell>
                  <TableCell>{pt ? 'Tipo' : 'Type'}</TableCell>
                  <TableCell align="right">{pt ? 'Nº Rotas' : 'Routes'}</TableCell>
                  <TableCell align="right">{pt ? 'Distância Total' : 'Total Distance'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((v, i) => (
                  <TableRow key={i} hover>
                    <TableCell style={{ fontWeight: 600 }}>{v.plate || '—'}</TableCell>
                    <TableCell>{v.type || '—'}</TableCell>
                    <TableCell align="right">{v.routeCount}</TableCell>
                    <TableCell align="right">{(v.totalDistance / 1000).toFixed(0)} km</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeReport === 'utilization' ? (
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Matrícula' : 'Plate'}</TableCell>
                  <TableCell align="right">{pt ? 'Dias activos' : 'Active days'}</TableCell>
                  <TableCell align="right">{pt ? 'Nº Rotas' : 'Routes'}</TableCell>
                  <TableCell align="right">{pt ? 'Distância' : 'Distance'}</TableCell>
                  <TableCell>{pt ? 'Taxa de utilização' : 'Utilization'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(reportData || []).map((v, i) => {
                  const pctColor = v.utilizationPct >= 70 ? '#16a34a' : v.utilizationPct >= 40 ? '#d97706' : '#dc2626';
                  return (
                    <TableRow key={i} hover>
                      <TableCell style={{ fontWeight: 600, fontFamily: 'Roboto Mono, monospace' }}>{v.plate || '—'}</TableCell>
                      <TableCell align="right">{v.activeDays} / {v.periodDays}</TableCell>
                      <TableCell align="right">{v.routeCount}</TableCell>
                      <TableCell align="right">{(v.totalDistance / 1000).toFixed(0)} km</TableCell>
                      <TableCell>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 150 }}>
                          <Box style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surf-head-bg, #f1f5f9)', overflow: 'hidden' }}>
                            <Box style={{ width: `${Math.min(100, v.utilizationPct)}%`, height: '100%', background: pctColor, borderRadius: 999 }} />
                          </Box>
                          <span style={{ fontSize: '0.74rem', fontWeight: 800, color: pctColor, fontFamily: 'Roboto Mono, monospace', minWidth: 36, textAlign: 'right' }}>{v.utilizationPct}%</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeReport === 'divergences' ? (() => {
          const rows = reportData?.rows || [];
          const totals = reportData?.totals || { loaded: 0, delivered: 0, divergence: 0, withDivergence: 0, count: 0 };
          const fmtL = (n) => n == null ? '—' : `${Number(n).toLocaleString(pt ? 'pt-PT' : 'en-GB')} L`;
          // Convenção: divergência = ENTREGUE − CARREGADO. Falta (net<0) = vermelho;
          // sobra (net>0) = azul. Backend devolve carregado−entregue, por isso negamos.
          const net = (d) => (d == null ? null : -Number(d));
          const divColor = (n) => n == null ? '#94a3b8' : (Math.abs(n) < 0.001 ? '#16a34a' : (n < 0 ? '#dc2626' : '#2563eb'));
          // Normaliza o zero (evita "-0 L" quando carregado == entregue) — trata
          // como 0 exacto o que estiver dentro de 0,001 (mesmo limiar da cor).
          const fmtDiv = (n) => {
            if (n == null) return '—';
            const v = Math.abs(Number(n)) < 0.001 ? 0 : Number(n);
            return `${v > 0 ? '+' : ''}${v.toLocaleString(pt ? 'pt-PT' : 'en-GB')} L`;
          };
          return (
            <Box>
              {/* Resumo — FIXO no topo ao fazer scroll (sticky) */}
              <Box style={{
                position: 'sticky', top: -24, zIndex: 5, background: 'var(--surf-card, #ffffff)',
                margin: '-24px -24px 16px', padding: '24px 24px 14px',
                borderBottom: '1px solid #eef2f6', boxShadow: '0 6px 12px -8px rgba(15,23,42,0.18)',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12,
              }}>
                {[
                  { label: pt ? 'OTs' : 'Orders', value: String(totals.count), color: 'var(--surf-muted, #64748b)' },
                  { label: pt ? 'Total carregado' : 'Total loaded', value: fmtL(totals.loaded), color: '#2563eb' },
                  { label: pt ? 'Total entregue' : 'Total delivered', value: fmtL(totals.delivered), color: '#16a34a' },
                  { label: pt ? 'Divergência total' : 'Total divergence', value: fmtDiv(net(totals.divergence)), color: divColor(net(totals.divergence)) },
                  { label: pt ? 'Com divergência' : 'With divergence', value: String(totals.withDivergence), color: '#e11d48' },
                ].map((c, i) => (
                  <Paper key={i} style={{ padding: 14, borderRadius: 12, borderLeft: `4px solid ${c.color}` }}>
                    <Typography style={{ fontSize: '0.62rem', color: 'var(--surf-muted, #64748b)', textTransform: 'uppercase', fontWeight: 700 }}>{c.label}</Typography>
                    <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--surf-strong, #1e293b)' }}>{c.value}</Typography>
                  </Paper>
                ))}
              </Box>
              {rows.length === 0 ? (
                <Typography style={{ textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', padding: 40 }}>
                  {pt ? 'Sem OTs de combustível com entrega registada no período.' : 'No delivered fuel orders in the period.'}
                </Typography>
              ) : (
                <TableContainer component={Paper} style={{ borderRadius: 12 }}>
                  <Table size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{pt ? 'Data' : 'Date'}</TableCell>
                        <TableCell>{pt ? 'OT' : 'Order'}</TableCell>
                        <TableCell>{pt ? 'Veículo' : 'Vehicle'}</TableCell>
                        <TableCell>{pt ? 'Motorista' : 'Driver'}</TableCell>
                        <TableCell align="right">{pt ? 'Carregado' : 'Loaded'}</TableCell>
                        <TableCell align="right">{pt ? 'Entregue' : 'Delivered'}</TableCell>
                        <TableCell align="right" title={pt ? '− falta · + sobra (entregue − carregado)' : '− short · + surplus (delivered − loaded)'}>{pt ? 'Divergência' : 'Divergence'}</TableCell>
                        <TableCell>{pt ? 'Motivo' : 'Reason'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell style={{ fontSize: '0.72rem' }}>{r.date ? new Date(r.date).toLocaleDateString('pt-PT') : '—'}</TableCell>
                          <TableCell style={{ fontWeight: 600 }}>{r.orderNumber || '—'}</TableCell>
                          <TableCell style={{ fontSize: '0.72rem', fontFamily: 'Roboto Mono, monospace' }}>{r.vehiclePlate || '—'}</TableCell>
                          <TableCell style={{ fontSize: '0.72rem' }}>{r.driverName || '—'}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace' }}>{fmtL(r.loaded)}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace' }}>{fmtL(r.delivered)}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace', fontWeight: 800, color: divColor(net(r.divergence)) }}>
                            {fmtDiv(net(r.divergence))}
                          </TableCell>
                          <TableCell style={{ fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reason || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })() : activeReport === 'litresByStation' ? (() => {
          const loading = reportData?.loading || [];
          const delivery = reportData?.delivery || [];
          const totals = reportData?.totals || {};
          const fmtL = (n) => `${Number(n || 0).toLocaleString(pt ? 'pt-PT' : 'en-GB')} L`;
          const stationTable = (title, rows, litresTotal, color) => (
            <Box style={{ flex: 1, minWidth: 300 }}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--surf-strong, #1e293b)' }}>{title}</Typography>
                <span style={{ fontSize: '0.78rem', fontWeight: 800, color, fontFamily: 'Roboto Mono, monospace' }}>{fmtL(litresTotal)}</span>
              </Box>
              {rows.length === 0 ? (
                <Typography style={{ textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', padding: 24, fontSize: '0.8rem' }}>{pt ? 'Sem dados no período' : 'No data in the period'}</Typography>
              ) : (
                <TableContainer component={Paper} style={{ borderRadius: 12 }}>
                  <Table size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{pt ? 'Posto / Terminal' : 'Station / Terminal'}</TableCell>
                        <TableCell align="right">{pt ? 'OTs' : 'Orders'}</TableCell>
                        <TableCell align="right">{pt ? 'Litros' : 'Litres'}</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.76rem' }}>{r.station}</TableCell>
                          <TableCell align="right">{r.count}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace', fontWeight: 700 }}>{fmtL(r.litres)}</TableCell>
                          <TableCell align="right" style={{ fontFamily: 'Roboto Mono, monospace', color }}>{litresTotal > 0 ? Math.round((r.litres / litresTotal) * 100) : 0}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
          return (
            <Box style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {stationTable(pt ? '⛽ Enchimento — por terminal' : '⛽ Loading — by terminal', loading, totals.loadingLitres, '#2563eb')}
              {stationTable(pt ? '📍 Entrega de produto — por posto' : '📍 Delivery — by station', delivery, totals.deliveryLitres, '#16a34a')}
            </Box>
          );
        })() : activeReport === 'orders' ? (
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Nº Ordem' : 'Order #'}</TableCell>
                  <TableCell>{pt ? 'Recolha' : 'Pickup'}</TableCell>
                  <TableCell>{pt ? 'Entrega' : 'Delivery'}</TableCell>
                  <TableCell>{pt ? 'Carga' : 'Cargo'}</TableCell>
                  <TableCell>{pt ? 'Estado' : 'Status'}</TableCell>
                  <TableCell align="right">{pt ? 'Tempo serviço' : 'Service time'}</TableCell>
                  <TableCell align="right">{pt ? 'Preço' : 'Price'}</TableCell>
                  <TableCell>{pt ? 'Data' : 'Date'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((o, i) => {
                  const sc = { pending: '#94a3b8', assigned: '#3b82f6', in_transit: '#f59e0b', delivered: '#22c55e', cancelled: '#ef4444' };
                  // Coluna nova "Tempo serviço" — mostra actualDurationMin
                  // formatado (que após a Fase 1A é o tempo facturável
                  // quando há serviceStartedAt; senão é arrived→completed).
                  // Marca o ícone âmbar 🛠 quando há serviceStartedAt para
                  // o operador distinguir os relatórios novos dos legacy.
                  const dur = o.assistanceReport?.actualDurationMin;
                  const billable = !!o.assistanceReport?.serviceStartedAt;
                  const durStr = (dur != null && Number.isFinite(dur))
                    ? (dur >= 60
                        ? `${Math.floor(dur/60)}h ${dur%60 > 0 ? (dur%60) + 'min' : ''}`.trim()
                        : `${dur} min`)
                    : '—';
                  return (
                    <TableRow key={i} hover>
                      <TableCell style={{ fontWeight: 600 }}>{o.orderNumber}</TableCell>
                      <TableCell style={{ fontSize: '0.72rem' }}>{o.pickupName || '—'}</TableCell>
                      <TableCell style={{ fontSize: '0.72rem' }}>{o.deliveryName || '—'}</TableCell>
                      <TableCell style={{ fontSize: '0.72rem' }}>{o.cargoDescription || '—'}</TableCell>
                      <TableCell>
                        <Chip label={otStatusText(o.status, pt)} size="small"
                          style={{ height: 20, fontSize: '0.6rem', fontWeight: 700,
                            background: `${sc[o.status] || '#94a3b8'}20`, color: sc[o.status] || '#64748b' }} />
                      </TableCell>
                      <TableCell align="right" style={{ fontSize: '0.7rem', color: billable ? '#b45309' : '#475569' }}
                        title={billable
                          ? (pt ? 'Tempo facturável (serviço)' : 'Billable (service)')
                          : (pt ? 'Tempo no local (sem início de serviço)' : 'On-site (no service start)')}>
                        {dur != null ? (billable ? `🛠 ${durStr}` : durStr) : '—'}
                      </TableCell>
                      <TableCell align="right">{o.agreedPrice ? `${Number(o.agreedPrice).toFixed(0)}€` : '—'}</TableCell>
                      <TableCell style={{ fontSize: '0.72rem' }}>
                        {o.createdAt ? new Date(o.createdAt).toLocaleDateString('pt-PT') : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeReport === 'serviceTimes' ? (
          /* Tempos de serviço — separa espera (arrived→started) do
              facturável (started→completed). Linha de totais no topo,
              uma linha por OT em baixo. Em curso destaca-se em âmbar. */
          (() => {
            const fmtMin = (m) => {
              if (m == null || !Number.isFinite(m)) return '—';
              if (m < 60) return `${m} min`;
              const h = Math.floor(m / 60), mm = m % 60;
              return mm > 0 ? `${h}h ${mm}min` : `${h}h`;
            };
            const fmtClock = (iso) => iso
              ? new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
              : '—';
            const totals = reportData.reduce((acc, r) => ({
              count: acc.count + 1,
              wait: acc.wait + (r.waitMin || 0),
              service: acc.service + (r.serviceMin || 0),
              inProgress: acc.inProgress + (r.inProgress ? 1 : 0),
            }), { count: 0, wait: 0, service: 0, inProgress: 0 });
            return (
              <>
                {/* Painel de totais */}
                <Box style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  {[
                    { label: pt ? 'OTs' : 'OTs', value: totals.count, color: 'var(--surf-title, #0f172a)' },
                    { label: pt ? 'Em curso' : 'In service', value: totals.inProgress, color: '#f59e0b' },
                    { label: pt ? 'Espera total' : 'Total wait', value: fmtMin(totals.wait), color: '#0ea5e9' },
                    { label: pt ? 'Serviço total' : 'Total service', value: fmtMin(totals.service), color: '#16a34a' },
                    {
                      label: pt ? 'Média serviço' : 'Avg service',
                      value: totals.count > 0 ? fmtMin(Math.round(totals.service / totals.count)) : '—',
                      color: '#7c3aed',
                    },
                  ].map((t, i) => (
                    <Paper key={i} style={{ flex: '1 1 140px', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--surf-muted2, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t.label}
                      </Typography>
                      <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: t.color, marginTop: 4 }}>
                        {t.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>

                <TableContainer component={Paper} style={{ borderRadius: 12 }}>
                  <Table size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{pt ? 'OT' : 'OT'}</TableCell>
                        <TableCell>{pt ? 'Cliente final' : 'Customer'}</TableCell>
                        <TableCell>{pt ? 'Motorista' : 'Driver'}</TableCell>
                        <TableCell>{pt ? 'Viatura' : 'Vehicle'}</TableCell>
                        <TableCell>{pt ? 'Chegou' : 'Arrived'}</TableCell>
                        <TableCell>{pt ? 'Início serviço' : 'Service start'}</TableCell>
                        <TableCell>{pt ? 'Fim' : 'End'}</TableCell>
                        <TableCell align="right">{pt ? 'Espera' : 'Wait'}</TableCell>
                        <TableCell align="right">{pt ? 'Serviço' : 'Service'}</TableCell>
                        <TableCell align="right">{pt ? 'Total no local' : 'Total on-site'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell style={{ fontWeight: 600 }}>{r.orderNumber || `OT-${r.orderId}`}</TableCell>
                          <TableCell style={{ fontSize: '0.72rem' }}>{r.customer}</TableCell>
                          <TableCell style={{ fontSize: '0.72rem' }}>{r.driver}</TableCell>
                          <TableCell style={{ fontSize: '0.72rem' }}>{r.vehicle}</TableCell>
                          <TableCell style={{ fontSize: '0.7rem', color: '#0ea5e9' }}>{fmtClock(r.arrivedAt)}</TableCell>
                          <TableCell style={{ fontSize: '0.7rem', color: '#f59e0b' }}>{fmtClock(r.serviceStartedAt)}</TableCell>
                          <TableCell style={{ fontSize: '0.7rem', color: r.inProgress ? '#f59e0b' : '#16a34a' }}>
                            {r.inProgress ? (pt ? 'em curso' : 'in progress') : fmtClock(r.completedAt)}
                          </TableCell>
                          <TableCell align="right">{fmtMin(r.waitMin)}</TableCell>
                          <TableCell align="right" style={{ fontWeight: 700, color: r.inProgress ? '#f59e0b' : '#16a34a' }}>
                            {fmtMin(r.serviceMin)}
                          </TableCell>
                          <TableCell align="right">{fmtMin(r.totalOnSite)}</TableCell>
                        </TableRow>
                      ))}
                      {reportData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center" style={{ padding: 24, color: 'var(--surf-muted2, #94a3b8)', fontStyle: 'italic' }}>
                            {pt
                              ? 'Sem OTs com tempo de serviço registado neste período.'
                              : 'No OTs with recorded service time in this period.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()
        ) : activeReport === 'daily' ? (
          /* Daily summary — one row per (driver, vehicle) for the chosen day */
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Dia' : 'Day'}</TableCell>
                  <TableCell>{pt ? 'Matrícula' : 'Plate'}</TableCell>
                  <TableCell>{pt ? 'Condutor' : 'Driver'}</TableCell>
                  <TableCell>{pt ? 'Início (local)' : 'Start (location)'}</TableCell>
                  <TableCell>{pt ? 'Fim (local)' : 'End (location)'}</TableCell>
                  <TableCell align="right">{pt ? 'Km' : 'Km'}</TableCell>
                  <TableCell align="right">{pt ? 'OTs do dia' : 'OTs'}</TableCell>
                  <TableCell align="right">{pt ? 'Executadas' : 'Executed'}</TableCell>
                  <TableCell align="right">{pt ? 'Não executadas' : 'Not executed'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{r.date}</TableCell>
                    <TableCell style={{ fontFamily: 'Roboto Mono, monospace', fontWeight: 700 }}>{r.vehiclePlate}</TableCell>
                    <TableCell>{r.driverName}</TableCell>
                    <TableCell style={{ fontSize: '0.74rem', color: '#15803d' }}>{r.startName}</TableCell>
                    <TableCell style={{ fontSize: '0.74rem', color: '#b91c1c' }}>{r.endName}</TableCell>
                    <TableCell align="right" style={{ fontWeight: 700 }}>{r.distanceKm ? r.distanceKm.toFixed(1) : '—'}</TableCell>
                    <TableCell align="right">{r.totalOTs}</TableCell>
                    <TableCell align="right" style={{ color: '#16a34a', fontWeight: 700 }}>{r.deliveredOTs}</TableCell>
                    <TableCell align="right" style={{ color: r.notDeliveredOTs > 0 ? '#dc2626' : '#94a3b8', fontWeight: r.notDeliveredOTs > 0 ? 700 : 400 }}>
                      {r.notDeliveredOTs}
                    </TableCell>
                  </TableRow>
                ))}
                {reportData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: 24, color: 'var(--surf-muted2, #94a3b8)', fontStyle: 'italic' }}>
                      {pt ? 'Sem registos para este dia.' : 'No records for this day.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* Compliance / default */
          <TableContainer component={Paper} style={{ borderRadius: 12 }}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{pt ? 'Rota' : 'Route'}</TableCell>
                  <TableCell align="right">{pt ? 'Distância' : 'Distance'}</TableCell>
                  <TableCell align="right">{pt ? 'Duração' : 'Duration'}</TableCell>
                  <TableCell align="right">{pt ? 'Paragens' : 'Stops'}</TableCell>
                  <TableCell>{pt ? 'Estado' : 'Status'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell style={{ fontWeight: 600 }}>{r.name || '—'}</TableCell>
                    <TableCell align="right">{r.distance ? `${(r.distance/1000).toFixed(0)} km` : '—'}</TableCell>
                    <TableCell align="right">{r.duration ? `${Math.floor(r.duration/3600)}h${Math.floor((r.duration%3600)/60)}m` : '—'}</TableCell>
                    <TableCell align="right">{r.stops || 0}</TableCell>
                    <TableCell>
                      <Chip label={routeStatusLabel(r.status)} size="small"
                        style={{ height: 20, fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

export default ReportsView;
